import React, { createContext, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import axios from 'axios';

// Import themes
import { themeMap, blueTheme } from '../themes';

// Create context
export const ThemeLayoutContext = createContext();

// Provider component
export const ThemeLayoutProvider = ({ children }) => {
  // State for theme and layout
  const [currentTheme, setCurrentTheme] = useState('blue');
  const [currentLayout, setCurrentLayout] = useState('classic');
  const [themeObject, setThemeObject] = useState(blueTheme);
  
  // Fetch theme and layout settings from API
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/settings');
        
        // Set theme if available
        if (response.data.siteTheme && themeMap[response.data.siteTheme]) {
          setCurrentTheme(response.data.siteTheme);
          setThemeObject(themeMap[response.data.siteTheme]);
        }
        
        // Set layout if available
        if (response.data.homeLayout) {
          setCurrentLayout(response.data.homeLayout);
        }
      } catch (error) {
        console.error('Error fetching theme/layout settings:', error);
      }
    };
    
    fetchSettings();
    
    // Listen for settings updates
    const handleSettingsUpdate = (event) => {
      if (event.detail) {
        // Update theme if changed
        if (event.detail.siteTheme && themeMap[event.detail.siteTheme]) {
          setCurrentTheme(event.detail.siteTheme);
          setThemeObject(themeMap[event.detail.siteTheme]);
        }
        
        // Update layout if changed
        if (event.detail.homeLayout) {
          setCurrentLayout(event.detail.homeLayout);
        }
      }
    };
    
    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    
    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsUpdate);
    };
  }, []);
  
  // Function to change theme
  const changeTheme = async (themeName) => {
    if (!themeMap[themeName]) return;
    
    try {
      // Update theme in state
      setCurrentTheme(themeName);
      setThemeObject(themeMap[themeName]);
      
      // Save to backend
      await axios.put('/api/settings', { siteTheme: themeName });
      
      // Dispatch event for other components to update
      window.dispatchEvent(new CustomEvent('settingsUpdated', { 
        detail: { siteTheme: themeName } 
      }));
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };
  
  // Function to change layout
  const changeLayout = async (layoutName) => {
    try {
      // Update layout in state
      setCurrentLayout(layoutName);
      
      // Save to backend
      await axios.put('/api/settings', { homeLayout: layoutName });
      
      // Dispatch event for other components to update
      window.dispatchEvent(new CustomEvent('settingsUpdated', { 
        detail: { homeLayout: layoutName } 
      }));
    } catch (error) {
      console.error('Error updating layout:', error);
    }
  };
  
  // Context value
  const contextValue = {
    currentTheme,
    currentLayout,
    changeTheme,
    changeLayout,
    availableThemes: Object.keys(themeMap),
    availableLayouts: ['classic', 'modern', 'minimalist']
  };
  
  return (
    <ThemeLayoutContext.Provider value={contextValue}>
      <ThemeProvider theme={themeObject}>
        {children}
      </ThemeProvider>
    </ThemeLayoutContext.Provider>
  );
};

ThemeLayoutProvider.propTypes = {
  children: PropTypes.node.isRequired
};

// Custom hook for using the theme layout context
export const useThemeLayout = () => useContext(ThemeLayoutContext);
